import { useState, useEffect } from 'react';

interface ImageFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

interface StoredImageData {
  id: string;
  name: string;
  size: number;
  preview: string;
  timestamp: number;
}

export const useImageStorage = (storageKey: string) => {
  const [images, setImages] = useState<ImageFile[]>([]);

  // Load images from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsedData: StoredImageData[] = JSON.parse(stored);
        
        // Filter out old images (older than 24 hours)
        const validImages = parsedData.filter(
          img => Date.now() - img.timestamp < 24 * 60 * 60 * 1000
        );

        // Convert back to ImageFile format (recreate File objects)
        const restoredImages: ImageFile[] = validImages.map(data => ({
          id: data.id,
          name: data.name,
          size: data.size,
          preview: data.preview,
          // Create a placeholder File object (for demo purposes)
          file: new File([''], data.name, { type: 'image/jpeg' })
        }));

        setImages(restoredImages);

        // Clean up localStorage if some images were too old
        if (validImages.length !== parsedData.length) {
          saveToStorage(restoredImages);
        }
      } catch (error) {
        console.error('Error loading images from storage:', error);
        localStorage.removeItem(storageKey);
      }
    }
  }, [storageKey]);

  const saveToStorage = (imagesToSave: ImageFile[]) => {
    try {
      const dataToStore: StoredImageData[] = imagesToSave.map(img => ({
        id: img.id,
        name: img.name,
        size: img.size,
        preview: img.preview,
        timestamp: Date.now()
      }));
      
      localStorage.setItem(storageKey, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving images to storage:', error);
    }
  };

  const updateImages = (newImages: ImageFile[]) => {
    setImages(newImages);
    saveToStorage(newImages);
  };

  const clearImages = () => {
    setImages([]);
    localStorage.removeItem(storageKey);
  };

  return {
    images,
    updateImages,
    clearImages
  };
};